import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../../lib/supabase';
import MemberDetailPanel from './MemberDetailPanel';

interface Member {
  id: string;
  first_name: string;
  last_name: string;
  business_name: string | null;
  email: string;
  phone_number: string | null;
  industry: string | null;
  company_size: string | null;
  client_volume: string | null;
  enquiry_frequency: string | null;
  interests: string | null;
  newsletter_opt_in: boolean;
  created_at: string;
}

type SortKey = 'created_at' | 'first_name' | 'business_name' | 'industry';
type SortDir = 'asc' | 'desc';

const MembersSection = () => {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [industryFilter, setIndustryFilter] = useState('');
  const [sizeFilter, setSizeFilter] = useState('');
  const [newsletterFilter, setNewsletterFilter] = useState<'' | 'yes' | 'no'>('');
  const [sortKey, setSortKey] = useState<SortKey>('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 12;

  useEffect(() => {
    const fetchMembers = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('signups')
        .select('*')
        .order('created_at', { ascending: false });
      if (!error && data) setMembers(data as Member[]);
      setLoading(false);
    };
    fetchMembers();
  }, []);

  const industries = useMemo(
    () => Array.from(new Set(members.map((m) => m.industry).filter(Boolean))) as string[],
    [members]
  );
  const companySizes = useMemo(
    () => Array.from(new Set(members.map((m) => m.company_size).filter(Boolean))) as string[],
    [members]
  );

  const filtered = useMemo(() => {
    let list = [...members];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (m) =>
          `${m.first_name} ${m.last_name}`.toLowerCase().includes(q) ||
          m.email.toLowerCase().includes(q) ||
          (m.business_name || '').toLowerCase().includes(q)
      );
    }
    if (industryFilter) list = list.filter((m) => m.industry === industryFilter);
    if (sizeFilter) list = list.filter((m) => m.company_size === sizeFilter);
    if (newsletterFilter === 'yes') list = list.filter((m) => m.newsletter_opt_in);
    if (newsletterFilter === 'no') list = list.filter((m) => !m.newsletter_opt_in);

    list.sort((a, b) => {
      let av = a[sortKey] ?? '';
      let bv = b[sortKey] ?? '';
      if (sortDir === 'asc') return av < bv ? -1 : av > bv ? 1 : 0;
      return av > bv ? -1 : av < bv ? 1 : 0;
    });
    return list;
  }, [members, search, industryFilter, sizeFilter, newsletterFilter, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => { setCurrentPage(1); }, [search, industryFilter, sizeFilter, newsletterFilter]);

  const thisMonth = useMemo(() => {
    const now = new Date();
    return members.filter((m) => {
      const d = new Date(m.created_at);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
  }, [members]);

  const newsletterCount = useMemo(() => members.filter((m) => m.newsletter_opt_in).length, [members]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortIcon = ({ col }: { col: SortKey }) => (
    <i className={`text-xs ml-1 ${sortKey === col ? 'text-ea-blue' : 'text-ea-gray/30'} ${sortKey === col && sortDir === 'desc' ? 'ri-arrow-down-s-line' : 'ri-arrow-up-s-line'}`}></i>
  );

  const exportCsv = () => {
    const headers = ['First Name','Last Name','Email','Business','Industry','Company Size','Client Volume','Enquiry Frequency','Newsletter','Joined'];
    const rows = filtered.map((m) => [
      m.first_name, m.last_name, m.email,
      m.business_name || '', m.industry || '',
      m.company_size || '',
      m.client_volume || '',
      m.enquiry_frequency || '',
      m.newsletter_opt_in ? 'Yes' : 'No',
      new Date(m.created_at).toLocaleDateString(),
    ]);
    const csv = [headers, ...rows].map((r) => r.map((v) => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'members.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-white text-2xl font-bold">Member Management</h1>
          <p className="text-ea-gray/60 text-sm mt-1">All registered signups, admin view only</p>
        </div>
        <button
          onClick={exportCsv}
          className="flex items-center gap-2 px-4 py-2 bg-ea-dark-card hover:bg-ea-dark-border border border-ea-dark-border rounded-xl text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-download-2-line text-sm"></i>
          Export CSV
        </button>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Members', value: loading ? '—' : members.length, icon: 'ri-group-line', color: 'text-ea-blue', bg: 'bg-ea-blue/10' },
          { label: 'Joined This Month', value: loading ? '—' : thisMonth, icon: 'ri-calendar-line', color: 'text-green-400', bg: 'bg-green-500/10' },
          { label: 'Newsletter Subs', value: loading ? '—' : newsletterCount, icon: 'ri-mail-check-line', color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
          { label: 'Filtered Results', value: loading ? '—' : filtered.length, icon: 'ri-filter-3-line', color: 'text-orange-400', bg: 'bg-orange-500/10' },
        ].map((stat) => (
          <div key={stat.label} className="bg-ea-dark-card border border-ea-dark-border rounded-2xl p-4 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl ${stat.bg} flex items-center justify-center shrink-0`}>
              <i className={`${stat.icon} ${stat.color} text-lg`}></i>
            </div>
            <div>
              <p className="text-white text-xl font-bold leading-none">{stat.value}</p>
              <p className="text-ea-gray/60 text-xs mt-0.5">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-48">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-ea-gray/50 text-sm"></i>
          <input
            type="text"
            placeholder="Search name, email or business..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-ea-dark-card border border-ea-dark-border rounded-xl text-white text-sm placeholder-ea-gray/40 focus:outline-none focus:border-ea-blue/40 transition-colors"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-ea-gray/50 hover:text-white cursor-pointer">
              <i className="ri-close-line text-sm"></i>
            </button>
          )}
        </div>

        <select
          value={industryFilter}
          onChange={(e) => setIndustryFilter(e.target.value)}
          className="px-3 py-2.5 bg-ea-dark-card border border-ea-dark-border rounded-xl text-sm text-ea-gray focus:outline-none focus:border-ea-blue/40 cursor-pointer"
        >
          <option value="">All Industries</option>
          {industries.map((i) => <option key={i} value={i}>{i}</option>)}
        </select>

        <select
          value={sizeFilter}
          onChange={(e) => setSizeFilter(e.target.value)}
          className="px-3 py-2.5 bg-ea-dark-card border border-ea-dark-border rounded-xl text-sm text-ea-gray focus:outline-none focus:border-ea-blue/40 cursor-pointer"
        >
          <option value="">All Sizes</option>
          {companySizes.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>

        <select
          value={newsletterFilter}
          onChange={(e) => setNewsletterFilter(e.target.value as '' | 'yes' | 'no')}
          className="px-3 py-2.5 bg-ea-dark-card border border-ea-dark-border rounded-xl text-sm text-ea-gray focus:outline-none focus:border-ea-blue/40 cursor-pointer"
        >
          <option value="">Newsletter: All</option>
          <option value="yes">Subscribed</option>
          <option value="no">Not Subscribed</option>
        </select>

        {(search || industryFilter || sizeFilter || newsletterFilter) && (
          <button
            onClick={() => { setSearch(''); setIndustryFilter(''); setSizeFilter(''); setNewsletterFilter(''); }}
            className="px-3 py-2.5 text-ea-gray/60 hover:text-white text-sm cursor-pointer transition-colors whitespace-nowrap"
          >
            <i className="ri-refresh-line mr-1.5"></i>Clear
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-ea-dark-card border border-ea-dark-border rounded-2xl overflow-hidden">
        {loading ? (
          <div className="space-y-0">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-5 py-4 border-b border-ea-dark-border last:border-0">
                <div className="w-8 h-8 rounded-full bg-ea-dark-border animate-pulse shrink-0"></div>
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 w-40 bg-ea-dark-border rounded animate-pulse"></div>
                  <div className="h-2.5 w-56 bg-ea-dark-border rounded animate-pulse"></div>
                </div>
                <div className="h-3 w-24 bg-ea-dark-border rounded animate-pulse"></div>
                <div className="h-3 w-20 bg-ea-dark-border rounded animate-pulse"></div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-14 h-14 rounded-2xl bg-ea-dark-border flex items-center justify-center mb-3">
              <i className="ri-group-line text-2xl text-ea-gray/40"></i>
            </div>
            <p className="text-white font-semibold text-sm">No members found</p>
            <p className="text-ea-gray/50 text-xs mt-1">Try adjusting your search or filters</p>
          </div>
        ) : (
          <>
            {/* Table head */}
            <div className="grid grid-cols-[2fr_2fr_1.5fr_1fr_1fr_auto] gap-3 px-5 py-3 border-b border-ea-dark-border bg-ea-navy/40">
              {([
                { label: 'Member', key: 'first_name' },
                { label: 'Business', key: 'business_name' },
                { label: 'Industry', key: 'industry' },
                { label: 'Size', key: null },
                { label: 'Joined', key: 'created_at' },
                { label: '', key: null },
              ] as { label: string; key: SortKey | null }[]).map(({ label, key }) => (
                <div
                  key={label}
                  className={`text-ea-gray/50 text-xs font-semibold uppercase tracking-wider flex items-center ${key ? 'cursor-pointer hover:text-ea-gray select-none' : ''}`}
                  onClick={() => key && handleSort(key)}
                >
                  {label}
                  {key && <SortIcon col={key} />}
                </div>
              ))}
            </div>

            {/* Rows */}
            <div>
              {paginated.map((member) => {
                const initials = `${member.first_name[0] || ''}${member.last_name[0] || ''}`.toUpperCase();
                return (
                  <div
                    key={member.id}
                    onClick={() => setSelectedMember(member)}
                    className="grid grid-cols-[2fr_2fr_1.5fr_1fr_1fr_auto] gap-3 items-center px-5 py-3.5 border-b border-ea-dark-border last:border-0 hover:bg-ea-navy/40 transition-colors cursor-pointer group"
                  >
                    {/* Name */}
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-ea-blue to-ea-blue-electric flex items-center justify-center shrink-0">
                        <span className="text-white text-xs font-bold">{initials}</span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-white text-sm font-medium truncate group-hover:text-ea-blue transition-colors">
                          {member.first_name} {member.last_name}
                        </p>
                        <p className="text-ea-gray/50 text-xs truncate">{member.email}</p>
                      </div>
                    </div>

                    {/* Business */}
                    <div className="min-w-0">
                      <p className="text-ea-gray text-sm truncate">{member.business_name || <span className="text-ea-gray/30 italic">—</span>}</p>
                    </div>

                    {/* Industry */}
                    <div>
                      {member.industry ? (
                        <span className="px-2.5 py-1 bg-ea-blue/10 text-ea-blue rounded-full text-xs font-medium whitespace-nowrap">
                          {member.industry}
                        </span>
                      ) : (
                        <span className="text-ea-gray/30 text-sm">—</span>
                      )}
                    </div>

                    {/* Company size + qualifying data */}
                    <div className="space-y-1">
                      <p className="text-ea-gray text-sm">{member.company_size || <span className="text-ea-gray/30">—</span>}</p>
                      {member.client_volume && (
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-group-line text-ea-blue/60 text-xs"></i>
                          </div>
                          <span className="text-ea-blue/70 text-xs truncate">{member.client_volume}</span>
                        </div>
                      )}
                      {member.enquiry_frequency && (
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-chat-poll-line text-ea-blue/60 text-xs"></i>
                          </div>
                          <span className="text-ea-blue/70 text-xs truncate">{member.enquiry_frequency}</span>
                        </div>
                      )}
                    </div>

                    {/* Joined */}
                    <div>
                      <p className="text-ea-gray/60 text-xs">
                        {new Date(member.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1">
                      {member.newsletter_opt_in && (
                        <div className="w-6 h-6 flex items-center justify-center" title="Newsletter subscriber">
                          <i className="ri-mail-check-line text-green-400 text-xs"></i>
                        </div>
                      )}
                      <div className="w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <i className="ri-arrow-right-s-line text-ea-blue text-sm"></i>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-4 border-t border-ea-dark-border">
                <p className="text-ea-gray/50 text-xs">
                  Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length}
                </p>
                <div className="flex items-center gap-1">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => p - 1)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg border border-ea-dark-border text-ea-gray hover:text-white hover:border-ea-blue/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line text-sm"></i>
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }).map((_, idx) => {
                    const page = idx + 1;
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-medium transition-colors cursor-pointer ${currentPage === page ? 'bg-ea-blue text-white' : 'text-ea-gray hover:text-white border border-ea-dark-border hover:border-ea-blue/30'}`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => p + 1)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg border border-ea-dark-border text-ea-gray hover:text-white hover:border-ea-blue/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line text-sm"></i>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail panel */}
      {selectedMember && (
        <MemberDetailPanel member={selectedMember} onClose={() => setSelectedMember(null)} />
      )}
    </div>
  );
};

export default MembersSection;
